import React, { useState, useCallback } from 'react';
import { createFeedback } from '../modules/Feedback/feedbackApi';

/* ── Utility: generate error reference ID ── */
function generateRefId() {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const hex = Math.random().toString(16).slice(2, 6);
  return `ERR-${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}-${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}-${hex}`;
}

/* ── Collect context for the bug report (plain, no hooks) ── */
function collectErrorContext() {
  try {
    const ua = navigator.userAgent || '';
    const browserMatch = ua.match(/(Chrome|Firefox|Safari|Edge)\/(\d+)/);
    const osMatch = ua.match(/(Windows NT \d+\.\d+|Mac OS X [\d_]+|Linux)/);
    const pathSeg = (window.location.pathname || '').split('/').filter(Boolean)[0] || 'Övrigt';
    return {
      url: window.location.pathname + window.location.search,
      browser: browserMatch ? `${browserMatch[1]} ${browserMatch[2]}` : ua.slice(0, 80),
      os: osMatch ? osMatch[1].replace(/_/g, '.') : 'Unknown',
      viewport: `${window.innerWidth}×${window.innerHeight}`,
      session_id: (sessionStorage.getItem('session_id') || localStorage.getItem('kanplan-session-id') || 'unknown').slice(0, 12),
      tenant_id: localStorage.getItem('kanplan-active-tenant-slug') || 'unknown',
      user_role: localStorage.getItem('kanplan-user-role') || 'unknown',
      module: pathSeg,
      console_errors: [],
      recent_api_calls: [],
    };
  } catch {
    return null;
  }
}

function truncate(str, n) {
  if (!str) return '';
  return str.length > n ? str.slice(0, n) + '…' : str;
}

/*
 * ErrorPage — branded KanPlan error fallback.
 * Used by ErrorBoundary (full-page) and optionally inline (compact variant).
 */
export default function ErrorPage({
  title = 'Något gick fel',
  description = 'Sidan kunde inte laddas. Försök igen eller kontakta support om problemet kvarstår.',
  errorMessage,
  error,
  showReload = true,
  showHome = true,
  homePath = '/home/overview',
  supportUrl,
  customAction,
  referenceId,
  compact = false,
}) {
  const [expanded, setExpanded] = useState(false);
  const displayMessage = errorMessage || error?.message || null;
  const refId = referenceId || generateRefId();

  /* ── Bug report state ── */
  const [reportOpen, setReportOpen] = useState(false);
  const [reportText, setReportText] = useState('');
  const [reportStatus, setReportStatus] = useState('idle'); // idle | sending | sent | failed
  const [reportId, setReportId] = useState(null);
  const [reportError, setReportError] = useState('');

  const handleReload = useCallback(() => window.location.reload(), []);
  const handleHome = useCallback(() => {
    window.location.href = homePath;
  }, [homePath]);

  const handleSendReport = useCallback(async () => {
    setReportStatus('sending');
    setReportError('');
    const ctx = collectErrorContext();
    const stack = error?.stack ? truncate(String(error.stack), 2000) : null;
    const title = truncate(displayMessage || 'Frontend-fel (ErrorBoundary)', 180);
    const descParts = [];
    if (reportText.trim()) descParts.push(reportText.trim());
    descParts.push(`Referens: ${refId}`);
    if (displayMessage) descParts.push(`Felmeddelande: ${displayMessage}`);
    if (stack) descParts.push(`Stack:\n${stack}`);
    const payload = {
      type: 'bug',
      title,
      expected_behavior: null,
      steps_to_reproduce: reportText.trim() ? [reportText.trim()] : [],
      severity: 'high',
      module: ctx?.module || 'Övrigt',
      context: ctx,
      description: descParts.join('\n\n'),
    };
    try {
      const result = await createFeedback(payload);
      setReportId(result?.id || null);
      setReportStatus('sent');
    } catch (e) {
      setReportError(e?.message || 'Kunde inte skicka rapporten');
      setReportStatus('failed');
    }
  }, [displayMessage, error, refId, reportText]);

  const mailtoFallback = useCallback(() => {
    const subject = encodeURIComponent(`Buggrapport – ${truncate(displayMessage || 'Frontend-fel', 80)}`);
    const bodyLines = [
      'Beskriv kort vad du gjorde:',
      reportText || '(lämna tomt om okänt)',
      '',
      `Referens: ${refId}`,
      `URL: ${window.location.href}`,
      `Felmeddelande: ${displayMessage || '-'}`,
    ];
    const body = encodeURIComponent(bodyLines.join('\n'));
    window.location.href = `mailto:support@kanplan.se?subject=${subject}&body=${body}`;
  }, [displayMessage, refId, reportText]);

  return (
    <div style={compact ? styles.compactWrapper : styles.pageWrapper}>
      <div style={styles.content}>

        {/* Logo */}
        {!compact && (
          <div style={styles.logo}>
            <div style={styles.logoIcon}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <rect x="3" y="3" width="6" height="6" rx="1.5" fill="#fff" />
                <rect x="11" y="3" width="6" height="6" rx="1.5" fill="#fff" opacity=".6" />
                <rect x="3" y="11" width="6" height="6" rx="1.5" fill="#fff" opacity=".6" />
                <rect x="11" y="11" width="6" height="6" rx="1.5" fill="#fff" opacity=".35" />
              </svg>
            </div>
            <span style={styles.logoText}>KanPlan</span>
          </div>
        )}

        {/* Error card */}
        <div
          style={compact ? styles.cardCompact : styles.card}
          role="alert"
          aria-live="assertive"
        >
          {/* Warning icon */}
          <div style={compact ? styles.warningIconCompact : styles.warningIcon}>
            <svg
              width={compact ? 22 : 28}
              height={compact ? 22 : 28}
              viewBox="0 0 24 24"
              fill="none"
              stroke="#D97706"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>

          {/* Title */}
          <h1 style={compact ? styles.titleCompact : styles.title}>{title}</h1>

          {/* Description */}
          <p style={compact ? styles.descriptionCompact : styles.description}>{description}</p>

          {/* Error detail box */}
          {displayMessage && (
            <div style={styles.detailBox}>
              <div style={styles.detailLabel}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#7E8FA3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="16" x2="12" y2="12" />
                  <line x1="12" y1="8" x2="12.01" y2="8" />
                </svg>
                <span style={styles.detailLabelText}>Felinformation</span>
              </div>
              <code
                style={{
                  ...styles.detailCode,
                  ...(expanded ? {} : styles.detailCodeClamped),
                }}
                onClick={() => setExpanded(!expanded)}
                aria-expanded={expanded}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setExpanded(!expanded); }}
              >
                {displayMessage}
              </code>
            </div>
          )}

          {/* Action buttons */}
          {customAction || (
            <div style={styles.actions}>
              {showReload && (
                <button type="button" style={styles.btnPrimary} onClick={handleReload}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="23 4 23 10 17 10" />
                    <path d="M20.49 15a9 9 0 11-2.12-9.36L23 10" />
                  </svg>
                  Ladda om
                </button>
              )}
              {showHome && !compact && (
                <button type="button" style={styles.btnSecondary} onClick={handleHome}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                    <polyline points="9 22 9 12 15 12 15 22" />
                  </svg>
                  Startsidan
                </button>
              )}
              {!compact && reportStatus !== 'sent' && (
                <button
                  type="button"
                  style={styles.btnSecondary}
                  onClick={() => setReportOpen((v) => !v)}
                  aria-expanded={reportOpen}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M8 2l1.88 1.88M16 2l-1.88 1.88M9 7.13v-1a3 3 0 0 1 6 0v1" />
                    <path d="M12 20c-3.3 0-6-2.7-6-6v-3a6 6 0 0 1 12 0v3c0 3.3-2.7 6-6 6z" />
                    <path d="M6 13H2M22 13h-4M6 17H3M21 17h-3M6 9H4M20 9h-2" />
                  </svg>
                  Skicka buggrapport
                </button>
              )}
            </div>
          )}

          {/* Bug report inline form */}
          {!compact && reportOpen && reportStatus !== 'sent' && (
            <div style={styles.reportBox}>
              <label htmlFor="kp-error-report-text" style={styles.reportLabel}>
                Beskriv kort vad du gjorde (valfritt)
              </label>
              <textarea
                id="kp-error-report-text"
                value={reportText}
                onChange={(e) => setReportText(e.target.value)}
                placeholder="T.ex. Jag klickade på 'Spara' i budgetmodulen..."
                rows={3}
                style={styles.reportTextarea}
                disabled={reportStatus === 'sending'}
              />
              <p style={styles.reportHint}>
                Felmeddelande, URL, webbläsare och referens-ID inkluderas automatiskt.
              </p>
              {reportStatus === 'failed' && (
                <div style={styles.reportErrorRow}>
                  <span style={styles.reportErrorText}>
                    {reportError || 'Kunde inte skicka rapporten.'}
                  </span>
                  <button type="button" style={styles.btnLink} onClick={mailtoFallback}>
                    Skicka via e-post istället
                  </button>
                </div>
              )}
              <div style={styles.reportActions}>
                <button
                  type="button"
                  style={styles.btnSecondarySm}
                  onClick={() => setReportOpen(false)}
                  disabled={reportStatus === 'sending'}
                >
                  Avbryt
                </button>
                <button
                  type="button"
                  style={styles.btnPrimarySm}
                  onClick={handleSendReport}
                  disabled={reportStatus === 'sending'}
                >
                  {reportStatus === 'sending' ? 'Skickar…' : 'Skicka rapport'}
                </button>
              </div>
            </div>
          )}

          {/* Bug report success */}
          {!compact && reportStatus === 'sent' && (
            <div style={styles.reportSuccess} role="status" aria-live="polite">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <span>
                Tack! Buggrapporten har skickats{reportId ? ` (ID: ${reportId})` : ''}.
              </span>
            </div>
          )}
        </div>

        {/* Support link */}
        {!compact && supportUrl && (
          <p style={styles.supportLink}>
            {'Behöver du hjälp? '}
            <a href={supportUrl} style={styles.supportAnchor}>Kontakta support</a>
          </p>
        )}

        {/* Reference */}
        {!compact && (
          <p style={styles.errorRef}>Referens: {refId}</p>
        )}
      </div>
    </div>
  );
}

/* ── Inline styles (self-contained, no external CSS needed) ── */
const styles = {
  pageWrapper: {
    fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
    background: '#F1F3F6',
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
    WebkitFontSmoothing: 'antialiased',
    margin: 0,
  },
  compactWrapper: {
    fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    WebkitFontSmoothing: 'antialiased',
  },
  content: {
    width: '100%',
    maxWidth: 480,
    textAlign: 'center',
  },

  /* Logo */
  logo: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 40,
  },
  logoIcon: {
    width: 36,
    height: 36,
    background: '#3B82F6',
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  logoText: {
    fontSize: 20,
    fontWeight: 600,
    color: '#1E293B',
    letterSpacing: '-0.3px',
  },

  /* Card */
  card: {
    background: '#FFFFFF',
    borderRadius: 14,
    boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.04)',
    padding: '40px 32px 36px',
    marginBottom: 24,
    textAlign: 'center',
  },
  cardCompact: {
    background: '#FFFFFF',
    borderRadius: 14,
    boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.04)',
    padding: '24px 20px',
    textAlign: 'center',
  },

  /* Warning icon */
  warningIcon: {
    width: 56,
    height: 56,
    background: '#FEF3C7',
    borderRadius: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 24px',
  },
  warningIconCompact: {
    width: 44,
    height: 44,
    background: '#FEF3C7',
    borderRadius: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 16px',
  },

  /* Typography */
  title: {
    fontSize: 22,
    fontWeight: 600,
    color: '#1E293B',
    letterSpacing: '-0.3px',
    margin: '0 0 8px',
  },
  titleCompact: {
    fontSize: 18,
    fontWeight: 600,
    color: '#1E293B',
    letterSpacing: '-0.3px',
    margin: '0 0 8px',
  },
  description: {
    fontSize: 15,
    color: '#64748B',
    lineHeight: 1.5,
    margin: '0 0 28px',
  },
  descriptionCompact: {
    fontSize: 14,
    color: '#64748B',
    lineHeight: 1.5,
    margin: '0 0 20px',
  },

  /* Error detail box */
  detailBox: {
    background: '#F0F2F5',
    borderRadius: 10,
    padding: '14px 16px',
    marginBottom: 28,
    textAlign: 'left',
  },
  detailLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginBottom: 6,
  },
  detailLabelText: {
    fontSize: 12,
    fontWeight: 500,
    color: '#7E8FA3',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  detailCode: {
    fontSize: 13,
    fontFamily: "'SF Mono', 'Fira Code', 'Cascadia Code', monospace",
    color: '#475569',
    wordBreak: 'break-all',
    lineHeight: 1.5,
    display: 'block',
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    padding: 0,
  },
  detailCodeClamped: {
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 4,
    WebkitBoxOrient: 'vertical',
  },

  /* Buttons */
  actions: {
    display: 'flex',
    gap: 12,
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  btnPrimary: {
    height: 40,
    padding: '0 24px',
    background: 'var(--kp-accent-fill, #2563EB)',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 500,
    fontFamily: 'inherit',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    whiteSpace: 'nowrap',
    transition: 'background 150ms',
  },
  btnSecondary: {
    height: 40,
    padding: '0 24px',
    background: '#FFFFFF',
    color: '#475569',
    border: '1px solid #E2E8F0',
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 500,
    fontFamily: 'inherit',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    whiteSpace: 'nowrap',
    transition: 'background 150ms, border-color 150ms',
  },

  /* Bug report inline form */
  reportBox: {
    marginTop: 20,
    background: '#F8FAFC',
    border: '1px solid #E2E8F0',
    borderRadius: 10,
    padding: '16px 16px 14px',
    textAlign: 'left',
  },
  reportLabel: {
    display: 'block',
    fontSize: 12,
    fontWeight: 600,
    color: '#475569',
    marginBottom: 8,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  reportTextarea: {
    width: '100%',
    boxSizing: 'border-box',
    minHeight: 72,
    padding: '10px 12px',
    borderRadius: 10,
    border: '1px solid #E2E8F0',
    background: '#FFFFFF',
    fontSize: 13.5,
    fontFamily: 'inherit',
    color: '#1E293B',
    resize: 'vertical',
    outline: 'none',
  },
  reportHint: {
    fontSize: 12,
    color: '#64748B',
    margin: '8px 0 0',
  },
  reportErrorRow: {
    marginTop: 10,
    padding: '8px 10px',
    background: '#FEF2F2',
    border: '1px solid #FECACA',
    borderRadius: 8,
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  reportErrorText: {
    fontSize: 12.5,
    color: '#B91C1C',
    fontWeight: 500,
  },
  btnLink: {
    background: 'none',
    border: 'none',
    padding: 0,
    color: '#2563EB',
    fontSize: 12.5,
    fontWeight: 600,
    cursor: 'pointer',
    textDecoration: 'underline',
    fontFamily: 'inherit',
  },
  reportActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
    marginTop: 12,
  },
  btnPrimarySm: {
    height: 34,
    padding: '0 14px',
    background: 'var(--kp-accent-fill, #2563EB)',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  btnSecondarySm: {
    height: 34,
    padding: '0 14px',
    background: '#FFFFFF',
    color: '#475569',
    border: '1px solid #E2E8F0',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  reportSuccess: {
    marginTop: 20,
    padding: '12px 14px',
    background: '#F0FDF4',
    border: '1px solid #BBF7D0',
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    fontSize: 13.5,
    color: '#14532D',
    fontWeight: 500,
    textAlign: 'left',
  },

  /* Footer */
  supportLink: {
    fontSize: 13,
    color: '#8D9DB0',
    margin: 0,
  },
  supportAnchor: {
    color: '#3B82F6',
    textDecoration: 'none',
    fontWeight: 500,
  },
  errorRef: {
    fontSize: 11,
    color: '#8D9DB0',
    fontFamily: "'SF Mono', 'Fira Code', 'Cascadia Code', monospace",
    marginTop: 16,
  },
};
